import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE } from "../lib/api";
import {
  FileText,
  ChevronRight,
  Upload,
  Download,
  Trash2,
  Loader2,
  CreditCard,
  ShieldCheck,
  FolderOpen,
  Briefcase,
  ScrollText,
} from "lucide-react";
import TwoFactorSettings from "../components/TwoFactorSettings";
import { Button } from "../components/ui/button";

const StatusBadge = ({ status }) => {
  const map = {
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    Closed: "bg-slate-50 text-slate-600 border-slate-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
    payment_failed: "bg-rose-50 text-rose-700 border-rose-200",
    refunded: "bg-slate-50 text-slate-600 border-slate-200",
    cancelled: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs uppercase tracking-[0.18em] border ${
        map[status] || map.Closed
      }`}
    >
      {status}
    </span>
  );
};

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

// ---------- Cases tab ----------
function CasesTab({ cases, copy, lang }) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (cases.length && !active) setActive(cases[0]);
  }, [cases, active]);

  if (cases.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="cases-empty">
        {copy.empty}
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-12 border border-slate-200">
      <div className="lg:col-span-5 border-e border-slate-200">
        {cases.map((c) => (
          <button
            key={c.id}
            onClick={() => setActive(c)}
            data-testid={`case-row-${c.id}`}
            className={`w-full text-start p-6 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors flex items-center justify-between ${
              active?.id === c.id ? "bg-slate-50" : ""
            }`}
          >
            <div>
              <p className="eyebrow">{c.practice_area}</p>
              <p className="font-display text-lg mt-1">{c.title}</p>
              <div className="mt-3 flex items-center gap-3">
                <StatusBadge status={c.status} />
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 rtl:rotate-180" />
          </button>
        ))}
      </div>
      <div className="lg:col-span-7 p-10">
        {active ? (
          <>
            <p className="eyebrow">{active.practice_area}</p>
            <h2 className="font-display text-3xl mt-2">{active.title}</h2>
            <div className="mt-4 flex items-center gap-3">
              <StatusBadge status={active.status} />
              <span className="text-sm text-slate-500">
                {copy.lastUpdate}: {new Date(active.last_update).toLocaleDateString(lang === "ar" ? "ar" : undefined)}
              </span>
            </div>
            <div className="mt-10">
              <p className="eyebrow mb-4">{copy.documents}</p>
              <ul className="border border-slate-200 divide-y divide-slate-200">
                {(active.documents || []).map((d, i) => (
                  <li key={`doc-${d.id || d.name}-${i}`} className="flex items-center justify-between p-4 hover:bg-slate-50">
                    <span className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{d.name}</span>
                    </span>
                    <span className="text-xs text-slate-500">{d.uploaded_at}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---------- Bookings tab ----------
function BookingsTab({ copy, lang }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    // Mount-only fetch. `api` is a stable module singleton; `setItems`
    // is a React state setter (guaranteed stable). No deps needed.
    api.get("/portal/bookings").then((r) => setItems(r.data)).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (items === null) {
    return <div className="p-16 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="bookings-empty">
        {copy.bookingsEmpty}
      </div>
    );
  }
  return (
    <ul className="border border-slate-200 divide-y divide-slate-200 bg-white" data-testid="bookings-list">
      {items.map((b) => (
        <li
          key={b.id}
          data-testid={`booking-row-${b.id}`}
          className="p-6 flex items-start justify-between gap-6 hover:bg-slate-50"
        >
          <div className="min-w-0">
            <p className="eyebrow">{b.practice_area}</p>
            <p className="font-display text-lg mt-1">
              {b.preferred_date} · {b.preferred_time}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {b.fee_amount} {b.currency} · {new Date(b.created_at).toLocaleString(lang === "ar" ? "ar" : undefined)}
            </p>
            {b.notes ? (
              <p className="mt-2 text-sm text-slate-700 line-clamp-2" dir="auto">{b.notes}</p>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusBadge status={b.status} />
            {b.status === "pending_payment" && b.payment_url ? (
              <a
                href={b.payment_url}
                className="text-xs uppercase tracking-[0.18em] border-b border-slate-900 pb-0.5"
              >
                {copy.completePayment}
              </a>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------- Contracts tab ----------
function ContractsTab({ copy, lang }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    // Mount-only fetch. See BookingsTab note above on stable refs.
    api.get("/portal/contracts").then((r) => setItems(r.data)).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (items === null) {
    return <div className="p-16 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="contracts-empty">
        {copy.contractsEmpty}
      </div>
    );
  }
  return (
    <ul className="border border-slate-200 divide-y divide-slate-200 bg-white" data-testid="contracts-list">
      {items.map((c) => (
        <li key={c.id} data-testid={`contract-row-${c.id}`} className="p-6 flex items-center justify-between hover:bg-slate-50">
          <div className="min-w-0">
            <p className="eyebrow">{c.contract_type} · {(c.language || "").toUpperCase()}</p>
            <p className="font-display text-lg mt-1 truncate">
              {c.parties || c.jurisdiction || c.contract_type}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {new Date(c.created_at).toLocaleString(lang === "ar" ? "ar" : undefined)}
            </p>
          </div>
          <a
            href={`/contracts?contractId=${c.id}`}
            data-testid={`contract-open-${c.id}`}
            className="text-xs uppercase tracking-[0.18em] border-b border-slate-900 pb-0.5"
          >
            {copy.openContract}
          </a>
        </li>
      ))}
    </ul>
  );
}

// ---------- Documents tab ----------
function DocumentsTab({ copy, lang, cases }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [caseId, setCaseId] = useState("");
  const [description, setDescription] = useState("");

  const reload = useCallback(() => {
    // `api` is a stable module singleton; `setItems` is a guaranteed
    // stable React state setter. Empty deps are intentional.
    api.get("/portal/documents").then((r) => setItems(r.data)).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caseId) fd.append("case_id", caseId);
      if (description) fd.append("description", description);
      await api.post("/portal/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDescription("");
      e.target.value = "";
      reload();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || copy.uploadError);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(copy.deleteConfirm)) return;
    try {
      await api.delete(`/portal/documents/${id}`);
      reload();
    } catch {
      setErr(copy.deleteError);
    }
  };

  return (
    <div>
      <div className="border border-slate-200 p-6 mb-8" data-testid="docs-uploader">
        <p className="eyebrow mb-3">{copy.uploadTitle}</p>
        <p className="text-sm text-slate-600 mb-4">{copy.uploadHint}</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <select
            data-testid="docs-case-select"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm"
          >
            <option value="">{copy.uploadCaseAny}</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <input
            type="text"
            data-testid="docs-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={copy.uploadDescription}
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm"
          />
        </div>
        <label
          className={`inline-flex items-center gap-2 px-5 h-11 border border-slate-900 cursor-pointer text-xs uppercase tracking-[0.18em] hover:bg-slate-900 hover:text-white transition-colors ${
            busy ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? copy.uploading : copy.uploadButton}
          <input
            type="file"
            data-testid="docs-file-input"
            className="hidden"
            onChange={onUpload}
            disabled={busy}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          />
        </label>
        {err ? <p className="mt-3 text-sm text-rose-700">{err}</p> : null}
      </div>

      {items === null ? (
        <div className="p-16 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="docs-empty">
          {copy.docsEmpty}
        </div>
      ) : (
        <ul className="border border-slate-200 divide-y divide-slate-200 bg-white" data-testid="docs-list">
          {items.map((d) => (
            <li
              key={d.id}
              data-testid={`doc-row-${d.id}`}
              className="p-5 flex items-center justify-between gap-4 hover:bg-slate-50"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.filename}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(d.size_bytes)} · {new Date(d.uploaded_at).toLocaleString(lang === "ar" ? "ar" : undefined)}
                  </p>
                  {d.description ? (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-1" dir="auto">{d.description}</p>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`${API_BASE}/portal/documents/${d.id}/download`}
                  data-testid={`doc-download-${d.id}`}
                  className="inline-flex items-center gap-1 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-slate-900 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  onClick={() => onDelete(d.id)}
                  data-testid={`doc-delete-${d.id}`}
                  className="inline-flex items-center gap-1 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-rose-700 hover:text-rose-700 transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Main Portal ----------
export default function Portal() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState("cases");

  useEffect(() => {
    // Mount-only fetch. State setters and module singletons are stable.
    api.get("/portal/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAr = lang === "ar";
  const copy = isAr
    ? {
        eyebrow: "بوابة العضو",
        welcome: (n) => `مرحباً، ${n || ""}.`,
        lead: "إدارة قضاياك وحجوزاتك وعقودك ومستنداتك في مكان واحد.",
        tabs: {
          cases: "قضاياي",
          bookings: "حجوزاتي",
          contracts: "عقودي",
          documents: "مستنداتي",
          security: "أمان الحساب",
        },
        statCases: "القضايا",
        statBookings: "الحجوزات",
        statDocs: "المستندات",
        empty: "لا توجد قضايا حتى الآن. سيقوم أحد الشركاء بتفعيل بوابتك بمجرد الانضمام.",
        bookingsEmpty: "لا توجد حجوزات في السجل. الحجز الآن يتم مباشرةً عبر واتساب.",
        completePayment: "إتمام الدفع",
        contractsEmpty: "لم تنشئ أي عقد بعد. ابدأ من صفحة عقود الذكاء الاصطناعي.",
        openContract: "فتح",
        documents: "المستندات",
        lastUpdate: "آخر تحديث",
        uploadTitle: "رفع مستند جديد",
        uploadHint: "PDF، صور، أو مستندات Office. الحد الأقصى 25 ميجابايت لكل ملف، 50 ملفاً لكل عميل.",
        uploadCaseAny: "— غير مرتبط بقضية —",
        uploadDescription: "وصف مختصر (اختياري)",
        uploadButton: "اختر ملفاً",
        uploading: "جارٍ الرفع...",
        uploadError: "فشل الرفع. حاول مرة أخرى.",
        docsEmpty: "لا توجد مستندات. ارفع أول ملف من الأعلى.",
        deleteConfirm: "هل تريد حذف هذا المستند نهائياً؟",
        deleteError: "تعذر الحذف.",
      }
    : {
        eyebrow: "Member Portal",
        welcome: (n) => `Welcome, ${n || ""}.`,
        lead: "Manage your matters, consultations, contracts, and documents — all in one place.",
        tabs: {
          cases: "My matters",
          bookings: "My bookings",
          contracts: "My contracts",
          documents: "My documents",
          security: "Security",
        },
        statCases: "Matters",
        statBookings: "Bookings",
        statDocs: "Documents",
        empty: "You have no matters yet. A partner will activate your portal once we onboard.",
        bookingsEmpty: "No bookings in your archive. Booking now happens directly via WhatsApp.",
        completePayment: "Complete payment",
        contractsEmpty: "You have not generated any contracts yet.",
        openContract: "Open",
        documents: "Documents",
        lastUpdate: "Last update",
        uploadTitle: "Upload a new document",
        uploadHint: "PDF, images, or Office documents. Max 25 MB per file, 50 files per client.",
        uploadCaseAny: "— Not linked to a matter —",
        uploadDescription: "Short description (optional)",
        uploadButton: "Choose file",
        uploading: "Uploading…",
        uploadError: "Upload failed. Please try again.",
        docsEmpty: "No documents yet. Upload your first one above.",
        deleteConfirm: "Permanently delete this document?",
        deleteError: "Could not delete the document.",
      };

  const [docCount, setDocCount] = useState(0);
  const [bookingCount, setBookingCount] = useState(0);
  useEffect(() => {
    // Mount-only counts. Failures are silenced — these are just
    // sidebar badges, not critical UI.
    api.get("/portal/documents").then((r) => setDocCount((r.data || []).length)).catch(() => {});
    api.get("/portal/bookings").then((r) => setBookingCount((r.data || []).length)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { id: "cases", label: copy.tabs.cases, icon: Briefcase },
    { id: "bookings", label: copy.tabs.bookings, icon: CreditCard },
    { id: "contracts", label: copy.tabs.contracts, icon: ScrollText },
    { id: "documents", label: copy.tabs.documents, icon: FolderOpen },
    { id: "security", label: copy.tabs.security, icon: ShieldCheck },
  ];

  return (
    <div data-testid="portal-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="grid lg:grid-cols-12 gap-8 mb-12">
        <div className="lg:col-span-7">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1 className="font-display text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight">
            {copy.welcome(user?.name)}
          </h1>
          <p className="mt-4 text-slate-600">{copy.lead}</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-3">
          <div className="border border-slate-200 p-5">
            <p className="eyebrow">{copy.statCases}</p>
            <p className="font-display text-3xl mt-2">{cases.length}</p>
          </div>
          <div className="border border-slate-200 p-5">
            <p className="eyebrow">{copy.statBookings}</p>
            <p className="font-display text-3xl mt-2">{bookingCount}</p>
          </div>
          <div className="border border-slate-200 p-5">
            <p className="eyebrow">{copy.statDocs}</p>
            <p className="font-display text-3xl mt-2">{docCount}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200 mb-8" data-testid="portal-tabs">
        {tabs.map((tt) => {
          const Icon = tt.icon;
          const active = tab === tt.id;
          return (
            <button
              key={tt.id}
              type="button"
              role="tab"
              data-testid={`portal-tab-${tt.id}`}
              onClick={() => setTab(tt.id)}
              className={`inline-flex items-center gap-2 px-4 h-11 text-xs uppercase tracking-[0.18em] -mb-px border-b-2 transition-colors ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tt.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div data-testid={`portal-pane-${tab}`}>
        {tab === "cases" && <CasesTab cases={cases} copy={copy} lang={lang} />}
        {tab === "bookings" && <BookingsTab copy={copy} lang={lang} />}
        {tab === "contracts" && <ContractsTab copy={copy} lang={lang} />}
        {tab === "documents" && <DocumentsTab copy={copy} lang={lang} cases={cases} />}
        {tab === "security" && (
          <div className="max-w-2xl">
            <TwoFactorSettings />
          </div>
        )}
      </div>
    </div>
  );
}
